import { TFunction } from 'i18next';
import { CSSProperties, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import {
  APERIO_EXTRA_LITRE_CENTS,
  APERIO_INCLUDED_LITRES,
  formatCents,
  PaymentPreference,
  PaymentPreferenceValue,
} from '../util/bookingPricing';
import { formatNumber } from '../i18n/format';
import {
  PLAN_STORAGE_PER_SEASON_CENTS,
  PlanCheck,
  aperioPlanName,
  planBreakdownSummary,
  planChooseLabel,
  planFeeCheck,
  planFinePrint,
  planIncludes,
  planMoney,
  planOnboardingCheck,
  planOneLiner,
  planPriceLine,
  planStorageCheck,
  planStorageExtra,
} from '../util/aperioPlanCopy';
import {
  AperioPlanOption,
  planCardPrice,
  PlanPriceView,
  yearlySavingsCents,
} from '../util/aperioPlans';

export interface AperioPlanOptionsProps {
  /** The plans the catalogue has for this vehicle (util/aperioPlans.ts). */
  options: AperioPlanOption[];
  prepayDiscountPercent: number;
  /** "CR-V" in "See how we priced your CR-V". */
  vehicleModel?: string | null;
  countryCode?: string | null;
  /**
   * The booking's payment preference, for the line beside the price toggle:
   * "$0 today" only while it is pay-after-service. Omitted: no line.
   */
  paymentPreference?: PaymentPreferenceValue;
  onChoose: (option: AperioPlanOption) => void;
  onPayPerVisit: () => void;
}

const PRICE_VIEWS: PlanPriceView[] = ['month', 'year'];

/** The customer's price view for this visit (tab): month unless they chose. */
const VIEW_STORAGE_KEY = 'apr-plans-view';

function storedView(): PlanPriceView {
  try {
    return window.sessionStorage.getItem(VIEW_STORAGE_KEY) === 'year'
      ? 'year'
      : 'month';
  } catch {
    return 'month';
  }
}

function storeView(view: PlanPriceView): void {
  try {
    window.sessionStorage.setItem(VIEW_STORAGE_KEY, view);
  } catch {
    // Storage blocked (private window, previews): the choice lasts the page.
  }
}

/** Read by assistive tech, never drawn (the classic clip pattern). */
const VISUALLY_HIDDEN: CSSProperties = {
  position: 'absolute',
  width: 1,
  height: 1,
  padding: 0,
  margin: -1,
  overflow: 'hidden',
  clip: 'rect(0 0 0 0)',
  whiteSpace: 'nowrap',
  border: 0,
};

function checksFor(option: AperioPlanOption): PlanCheck[] {
  // The onboarding includes the first visit's fee (Aperio): said on both
  // lines where there is a fee to include (not on All-Inclusive's $0).
  const includesFee = option.firstVisitFeeIncluded && option.feeCents > 0;
  const fee = planFeeCheck(
    option.feeCents,
    option.highUseFeeCents,
    option.visitsIncluded,
    includesFee,
  );
  const onboarding = planOnboardingCheck(option.onboardingCents, includesFee);
  // Same order on every card, so the three compare line by line.
  const storage = option.kind === 'allInclusive'
    ? planStorageCheck()
    : planStorageExtra(PLAN_STORAGE_PER_SEASON_CENTS);
  return [fee, storage, onboarding];
}

interface BreakdownRow {
  label: string;
  cells: string[];
  className?: string;
}

/** The per-vehicle breakdown behind the three cards, one column a plan. */
function breakdownRows(
  options: AperioPlanOption[],
  prepayDiscountPercent: number,
  t: TFunction,
): BreakdownRow[] {
  const rows: BreakdownRow[] = [];
  const b = (o: AperioPlanOption) => o.breakdown;
  // The tenant's own figures come with the breakdown (Tenant Settings);
  // the defaults only stand in for a server that does not send them yet.
  const settings = options.map(b).find((x) => x?.includedLitres != null);
  const included = formatNumber(
    Number(settings?.includedLitres ?? APERIO_INCLUDED_LITRES),
  );
  rows.push({
    label: t('plans.table.base', { included }),
    cells: options.map((o) => planMoney(b(o)?.basePriceCents ?? o.annualCents)),
  });
  const engine = options.find((o) => (b(o)?.engineSurchargeCents ?? 0) > 0);
  if (engine) {
    rows.push({
      label: b(engine)?.engineSurchargeReason === 'diesel'
        ? t('plans.table.diesel')
        : t('plans.table.european'),
      cells: options.map(
        (o) => `+${planMoney(b(o)?.engineSurchargeCents ?? 0)}`,
      ),
    });
  }
  const litres = options.find((o) => (b(o)?.extraLitres ?? 0) > 0);
  if (litres) {
    rows.push({
      // A pro-rata litre charge is rarely a round figure, so the surcharge
      // total is rounded (to the nearest $5 by default) and the line says so.
      label: t(
        (b(litres)?.surchargeRoundToCents ?? 0) > 1
          ? 'plans.table.litresRounded'
          : 'plans.table.litres',
        {
          included,
          litres: formatNumber(Number(b(litres)?.extraLitres ?? 0)),
          rate: planMoney(
            b(litres)?.extraLitreRateCents ?? APERIO_EXTRA_LITRE_CENTS,
          ),
          step: planMoney(b(litres)?.surchargeRoundToCents ?? 0),
        },
      ),
      cells: options.map(
        (o) => `+${planMoney(b(o)?.capacitySurchargeCents ?? 0)}`,
      ),
    });
  }
  if (engine || litres) {
    rows.push({
      label: t('plans.table.planYear'),
      cells: options.map((o) => planMoney(o.annualCents)),
      className: 'apr-plans__tr--sum',
    });
  }
  if (options.some((o) => o.monthlyPremiumCents > 0)) {
    rows.push({
      label: t('plans.table.payingMonthly'),
      cells: options.map((o) => (o.monthlyPremiumCents > 0
        ? `+${planMoney(o.monthlyPremiumCents)}`
        : '—')),
    });
  }
  rows.push({
    label: t('plans.table.paid'),
    cells: options.map((o) => (o.instalments > 1
      ? t('plans.table.monthly') : t('plans.table.once'))),
  });
  rows.push({
    label: t('plans.table.eachPayment'),
    cells: options.map((o) => planMoney(o.planCents)),
  });
  rows.push({
    label: t('plans.table.onboarding'),
    cells: options.map((o) => planMoney(o.onboardingCents)),
  });
  const visitsIncluded = options.find((o) => o.visitsIncluded)?.visitsIncluded;
  const highUse = options.find((o) => o.highUseFeeCents)?.highUseFeeCents;
  const firstIncluded = options.some((o) => o.firstVisitFeeIncluded);
  if (firstIncluded) {
    rows.push({
      label: t('plans.table.feeFirstVisit'),
      cells: options.map((o) => (o.firstVisitFeeIncluded && o.feeCents > 0
        ? t('plans.table.feeIncluded')
        : planMoney(o.nextVisitFeeCents))),
    });
  }
  let feeLabel = t('plans.table.feeVisit');
  if (firstIncluded) {
    feeLabel = visitsIncluded && highUse
      ? t('plans.table.feeVisits', { from: 2, to: visitsIncluded })
      : t('plans.table.feeLater');
  } else if (visitsIncluded && highUse) {
    feeLabel = t('plans.table.feeFirst', { count: visitsIncluded });
  }
  rows.push({
    label: feeLabel,
    cells: options.map((o) => planMoney(o.feeCents)),
  });
  if (visitsIncluded && highUse) {
    rows.push({
      label: t('plans.table.feeAfter', { n: visitsIncluded }),
      cells: options.map((o) => planMoney(o.highUseFeeCents ?? highUse)),
    });
  }
  rows.push({
    label: t('plans.due'),
    cells: options.map((o) => formatCents(o.dueCents)),
    className: 'apr-plans__tr--total',
  });
  if (prepayDiscountPercent > 0) {
    rows.push({
      label: t('plans.table.prepaid', { percent: prepayDiscountPercent }),
      cells: options.map((o) => formatCents(o.prepaidCents)),
    });
  }
  return rows;
}

/** "Monthly: 12 payments of $20.75 add up to $249." */
function monthlyNote(
  options: AperioPlanOption[],
  t: TFunction,
): string | null {
  const m = options.find((o) => o.kind === 'monthly');
  if (!m || m.instalments < 2) return null;
  const total = planMoney(m.termTotalCents);
  return m.lastInstalmentCents === m.planCents
    ? t('plans.monthlyNote', {
      n: m.instalments,
      amount: planMoney(m.planCents),
      total,
    })
    : t('plans.monthlyNoteLast', {
      n: m.instalments - 1,
      amount: planMoney(m.planCents),
      last: planMoney(m.lastInstalmentCents),
      total,
    });
}

/**
 * The Aperio plan step: three short plan cards (Yearly, Monthly,
 * All-Inclusive) with this vehicle's price, one shared "Every plan includes"
 * list, the per-vehicle breakdown behind a disclosure, and the fine print.
 * A "Price per month | Price per year" toggle above the cards picks how
 * they quote their price (month by default, remembered for the visit).
 * Rendered by RegularServiceList in place of the plan ProductCards, for the
 * Aperio preset only; every class is an `apr-*` hook styled in
 * public/aperio/theme.css.
 */
export default function AperioPlanOptions({
  options,
  prepayDiscountPercent,
  vehicleModel,
  countryCode,
  paymentPreference,
  onChoose,
  onPayPerVisit,
}: AperioPlanOptionsProps) {
  // Subscribes the step to language changes (the copy helpers read i18n).
  const { t } = useTranslation();
  const [view, setView] = useState<PlanPriceView>(storedView);
  const chooseView = (next: PlanPriceView) => {
    setView(next);
    storeView(next);
  };
  // Month view: always to the cent ($35.00); year view: whole dollars where
  // the figure is ($259), as the cards have always shown.
  const money = (cents: number) => (view === 'month'
    ? formatCents(cents)
    : planMoney(cents));
  // Beside the toggle, and only what is true for the booking's preference.
  let payNote: string | null = null;
  if (paymentPreference === PaymentPreference.payLater) {
    payNote = t('plans.view.payLater', { zero: planMoney(0) });
  } else if (
    paymentPreference === PaymentPreference.prepay
    && prepayDiscountPercent > 0
  ) {
    payNote = t('plans.fine.prepay', { percent: prepayDiscountPercent });
  }
  const savings = yearlySavingsCents(options);
  const rows = breakdownRows(options, prepayDiscountPercent, t);
  const note = monthlyNote(options, t);
  const model = (vehicleModel || '').trim();

  return (
    <div className="apr-plans-wrap">
      <section className="apr-plans" aria-labelledby="apr-plans-title">
        <h2 className="apr-plans__title" id="apr-plans-title">
          {model
            ? t('plans.titleModel', { model })
            : t('plans.title')}
        </h2>

        <div className="apr-plans__includes">
          <h3 className="apr-plans__kicker">{t('plans.includesHeading')}</h3>
          <ul className="apr-plans__checks apr-plans__checks--cols">
            {planIncludes().map((line) => (
              <li key={line.name}>
                {line.name}
                {line.detail && <span>{line.detail}</span>}
              </li>
            ))}
          </ul>
          <p className="apr-plans__not-included">{t('plans.notIncluded')}</p>
        </div>

        <div className="apr-plans__bar">
          <div
            className="apr-plans__view"
            role="radiogroup"
            aria-label={t('plans.view.label')}
          >
            {PRICE_VIEWS.map((v) => (
              <label
                key={v}
                className="apr-plans__view-opt"
                htmlFor={`apr-plans-view-${v}`}
              >
                <input
                  id={`apr-plans-view-${v}`}
                  type="radio"
                  className="apr-plans__view-input"
                  name="apr-plans-view"
                  value={v}
                  checked={view === v}
                  onChange={() => chooseView(v)}
                />
                <span>{t(`plans.view.${v}`)}</span>
              </label>
            ))}
          </div>
          {payNote && <p className="apr-plans__today">{payNote}</p>}
        </div>

        <div className="apr-plans__grid">
          {options.map((o) => {
            const featured = o.kind === 'yearly' && !!savings;
            const nameId = `apr-plan-${o.kind}`;
            const price = planCardPrice(o, options, view);
            const line = planPriceLine(o.kind, view, {
              planCents: o.planCents,
              savingsCents: savings,
              instalments: o.instalments,
              lastInstalmentCents: o.lastInstalmentCents,
              termTotalCents: o.termTotalCents,
            });
            return (
              <article
                key={o.kind}
                className={`apr-plans__card${featured
                  ? ' apr-plans__card--featured' : ''}`}
                aria-labelledby={nameId}
              >
                {featured && (
                  <span className="apr-plans__flag">{t('plans.bestValue')}</span>
                )}
                <h3 className="apr-plans__name" id={nameId}>
                  {aperioPlanName(o.kind)}
                </h3>
                <p className="apr-plans__price">
                  <span className="apr-plans__amount">
                    {money(price.amountCents)}
                  </span>
                  <span className="apr-plans__per">
                    {t(view === 'month' ? 'plans.per.month' : 'plans.per.year')}
                  </span>
                  {price.wasCents !== null && (
                    <s className="apr-plans__was">
                      <span style={VISUALLY_HIDDEN}>
                        {`${t('plans.view.wasLabel')} `}
                      </span>
                      {money(price.wasCents)}
                    </s>
                  )}
                </p>
                <p className="apr-plans__line">
                  {line && (
                    <Trans
                      t={t}
                      i18nKey={line.key}
                      values={line.values}
                      components={{ b: <b /> }}
                    />
                  )}
                  {line && o.kind === 'allInclusive' && ' '}
                  {o.kind === 'allInclusive' && planOneLiner(o.kind, {
                    feeCents: o.feeCents,
                    visitsIncluded: o.visitsIncluded,
                  })}
                </p>
                <ul className="apr-plans__checks">
                  {checksFor(o).map((c) => (
                    <li
                      key={c.text}
                      className={c.extra ? 'apr-plans__extra' : undefined}
                    >
                      {c.text}
                      {c.detail && <span>{c.detail}</span>}
                    </li>
                  ))}
                </ul>
                <p className="apr-plans__due">
                  <span>{t('plans.due')}</span>
                  <b>{formatCents(o.dueCents)}</b>
                </p>
                <button
                  type="button"
                  className="apr-plans__cta"
                  onClick={() => onChoose(o)}
                >
                  {planChooseLabel(o.kind)}
                </button>
              </article>
            );
          })}
        </div>

        <details className="apr-plans__details">
          <summary>{planBreakdownSummary(model)}</summary>
          <div className="apr-plans__table-wrap">
            <table className="apr-plans__table">
              <thead>
                <tr>
                  <td />
                  {options.map((o) => (
                    <th key={o.kind} scope="col">{aperioPlanName(o.kind)}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.label} className={r.className}>
                    <th scope="row">{r.label}</th>
                    {r.cells.map((c, i) => (
                      // eslint-disable-next-line react/no-array-index-key
                      <td key={i}>{c}</td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {note && <p className="apr-plans__note">{note}</p>}
        </details>

        <p className="apr-plans__fine">
          {planFinePrint(countryCode, prepayDiscountPercent)}
        </p>
        <p className="apr-plans__fine">{t('plans.disclosure')}</p>

        <button
          type="button"
          className="apr-plans__alt"
          onClick={onPayPerVisit}
        >
          {t('plans.payPerVisit')}
        </button>
      </section>
    </div>
  );
}
